import { AI_REQUEST_TIMEOUT_MS, apiClient } from './api';

/**
 * REV-513: the organization's own LLM key, and AI-assisted fix suggestions.
 *
 * The key itself is write-only across the whole API — there is no endpoint
 * that returns it, so there is deliberately no type here that can hold one
 * after it leaves the form.
 */

export interface AiCredentialStatus {
  configured: boolean;
  provider: string | null;
  /** Last 4 characters, for "is this the key I think it is". Never the key. */
  keyLast4: string | null;
  lastUsedAt: string | null;
  updatedAt: string | null;
  /**
   * REV-1127: false when an Owner or Admin switched every AI feature off for
   * the organization. The API then refuses every AI request (ai_disabled).
   */
  aiEnabled: boolean;
}

export interface AiFixRecommendation {
  recommendation: string;
  /** Always 'ai' — the UI must say so rather than presenting this as fact. */
  generatedBy: 'ai';
  /** Always false. Invariant 3: humans attest, AI assists. */
  verified: boolean;
  callId: string;
}

export const aiService = {
  async getCredentialStatus(orgId: string): Promise<AiCredentialStatus> {
    const res = await apiClient.get<{ success: boolean; data: AiCredentialStatus }>(
      `/v1/organizations/${orgId}/ai-credential`
    );
    return res.data;
  },

  async setCredential(orgId: string, apiKey: string): Promise<AiCredentialStatus> {
    const res = await apiClient.put<{ success: boolean; data: AiCredentialStatus }>(
      `/v1/organizations/${orgId}/ai-credential`,
      { apiKey }
    );
    return res.data;
  },

  async deleteCredential(orgId: string): Promise<void> {
    await apiClient.delete(`/v1/organizations/${orgId}/ai-credential`);
  },

  /**
   * Ask for an AI-written fix for one finding.
   *
   * POST because it is not idempotent and costs the organization money on
   * every call — it must never be prefetched or retried automatically.
   */
  async generateFixRecommendation(orgId: string, findingId: string): Promise<AiFixRecommendation> {
    // REV-973: goes through the AI proxy, so it waits past the API's own
    // provider deadline rather than the client-wide default.
    const res = await apiClient.post<{ success: boolean; data: AiFixRecommendation }>(
      `/v1/organizations/${orgId}/finding-instances/${findingId}/ai-recommendation`,
      undefined,
      { timeout: AI_REQUEST_TIMEOUT_MS }
    );
    return res.data;
  },
};

export default aiService;
